// Iter-207w — Unified email tracking history.
//
// Lists the most recent emails sent through the platform — invoices,
// reminders, NDF lifecycle, outgoing POs, monthly statements, etc. —
// with their "ouvert / non ouvert" status driven by the centralised
// 1×1 PNG tracking pixel embedded in every HTML body.
import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Eye, EyeOff, RefreshCw, Filter, Clock, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const KIND_LABELS = {
  invoice: { label: "Facture", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  invoice_reminder: { label: "Relance facture", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  payment_declaration: { label: "Déclaration", tone: "bg-sky-50 text-sky-700 border-sky-200" },
  cra_export: { label: "CRA Export", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  cra_late: { label: "CRA en retard", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  weekly_cra: { label: "CRA hebdo", tone: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  ndf_lifecycle: { label: "NDF", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  outgoing_po: { label: "Bon de commande", tone: "bg-purple-50 text-purple-700 border-purple-200" },
  freelance: { label: "Freelance", tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  mission: { label: "Mission", tone: "bg-lime-50 text-lime-700 border-lime-200" },
  monthly_statement: { label: "État mensuel", tone: "bg-teal-50 text-teal-700 border-teal-200" },
  user_admin: { label: "Compte / admin", tone: "bg-slate-50 text-slate-700 border-slate-200" },
  generic: { label: "Générique", tone: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};


const KindBadge = ({ kind }) => {
  const cfg = KIND_LABELS[kind] || { label: kind, tone: "bg-zinc-50 text-zinc-700 border-zinc-200" };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${cfg.tone}`}>
      {cfg.label}
    </span>
  );
};


export default function EmailTracking() {
  const [items, setItems] = useState([]);
  const [byKind, setByKind] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState("");
  const [onlyOpened, setOnlyOpened] = useState(false);
  // Iter-223 — search by recipient email + default sort by last activity.
  const [emailQuery, setEmailQuery] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  // Iter-224 — Detail modal: click a row → show all opens with date/time/UA/IP.
  const [detail, setDetail] = useState(null); // {token, ...full doc}
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (token) => {
    setDetailLoading(true);
    setDetail({ token, sent_subject: "", sent_to: [], open_events: [] });
    try {
      const r = await api.get(`/admin/email-tracking/${token}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Détail indisponible");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };


  // Debounce the email search so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEmail(emailQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [emailQuery]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterKind) params.set("kind", filterKind);
      if (onlyOpened) params.set("only_opened", "true");
      if (debouncedEmail) params.set("q_email", debouncedEmail);
      params.set("sort_by", "last_activity");
      params.set("limit", "200");
      const r = await api.get(`/admin/email-tracking?${params.toString()}`);
      setItems(r.data.items || []);
      setByKind(r.data.by_kind || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement échoué");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filterKind, onlyOpened, debouncedEmail]);

  const overall = useMemo(() => {
    const sent = items.length;
    const opened = items.filter((it) => it.opened_at).length;
    const rate = sent > 0 ? Math.round((opened / sent) * 100) : 0;
    return { sent, opened, rate };
  }, [items]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px] mx-auto space-y-5" data-testid="email-tracking-page">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight flex items-center gap-2">
            <Mail size={20} /> Tracking des emails
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Suivi d'ouverture (pixel 1×1) pour tous les envois — factures, relances, NDF, BC, état mensuel, etc.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="email-tracking-refresh">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Overall summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="email-tracking-summary">
        <Card label="Total envoyés" value={overall.sent} tone="zinc" />
        <Card label="Ouverts" value={overall.opened} tone="emerald" />
        <Card label="Taux d'ouverture" value={`${overall.rate}%`} tone={overall.rate >= 50 ? "emerald" : "amber"} />
      </div>

      {/* Per-kind breakdown */}
      {Object.keys(byKind).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="email-tracking-by-kind">
          {Object.entries(byKind).map(([k, v]) => {
            const rate = v.sent > 0 ? Math.round((v.opened / v.sent) * 100) : 0;
            return (
              <div key={k} className="px-2.5 py-1 bg-white border border-zinc-200 rounded text-xs">
                <KindBadge kind={k} />
                <span className="ml-2 font-mono text-zinc-700">{v.opened}/{v.sent}</span>
                <span className="ml-1 text-zinc-400">({rate}%)</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-zinc-200 rounded-lg p-2.5">
        <Filter size={14} className="text-zinc-400" />
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value)}
          className="h-8 px-2 text-xs border border-zinc-200 rounded-md bg-white"
          data-testid="email-tracking-filter-kind"
        >
          <option value="">Toutes catégories</option>
          {Object.entries(KIND_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={onlyOpened}
            onChange={(e) => setOnlyOpened(e.target.checked)}
            data-testid="email-tracking-filter-opened"
            className="h-3.5 w-3.5 accent-emerald-600"
          />
          Uniquement ouverts
        </label>
        {/* Iter-223 — recipient email search. */}
        <input
          type="search"
          value={emailQuery}
          onChange={(e) => setEmailQuery(e.target.value)}
          placeholder="Rechercher par adresse email (À ou Cc)…"
          data-testid="email-tracking-search-email"
          className="h-8 px-2 text-xs border border-zinc-200 rounded-md bg-white min-w-[260px] flex-1"
        />
        <span className="ml-auto text-[11px] text-zinc-400 font-mono">{items.length} résultat(s)</span>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {items.length === 0 && !loading && (
          <div className="px-4 py-10 text-sm text-zinc-400 text-center">Aucun email tracké pour ce filtre.</div>
        )}
        {items.map((it) => (
          <button
            type="button"
            key={it.token}
            onClick={() => openDetail(it.token)}
            data-testid={`email-tracking-row-${it.token}`}
            className="w-full text-left border-b border-zinc-100 last:border-b-0 px-4 py-2.5 text-xs hover:bg-emerald-50/40 focus:bg-emerald-50/40 focus:outline-none transition-colors"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <KindBadge kind={it.kind} />
              <span className="font-mono text-zinc-700" title="Date d'envoi">{(it.sent_at || "").slice(0, 19).replace("T", " ")}</span>
              <span className="text-zinc-900 font-medium truncate max-w-xl">{it.sent_subject || "—"}</span>
              <span className="ml-auto inline-flex items-center gap-1.5">
                {it.opened_at ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" title={`Ouvert ${it.open_count || 1}× — dernière ouverture ${(it.last_open_at || "").slice(0, 19).replace("T", " ")}`}>
                    <Eye size={12} /> Ouvert ({it.open_count || 1}×)
                    {it.last_open_at && (
                      <span className="ml-1 font-mono text-emerald-600/80 text-[10px]">{(it.last_open_at || "").slice(0, 19).replace("T", " ")}</span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-zinc-400">
                    <EyeOff size={12} /> Non ouvert
                  </span>
                )}
              </span>
            </div>
            <div className="text-zinc-500 mt-1">
              <span className="mr-3">À : {(it.sent_to || []).join(", ") || "—"}</span>
              {(it.sent_cc || []).length > 0 && <span className="mr-3">Cc : {(it.sent_cc || []).join(", ")}</span>}
              {it.sent_by_name && <span className="text-zinc-400">· par {it.sent_by_name}</span>}
            </div>
          </button>
        ))}
      </div>

      {/* Iter-224 — Detail modal: click on a row to see every open with date/time/UA. */}
      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="email-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Mail size={16} className="text-brand-orange" />
              <span className="truncate">{detail?.sent_subject || "Email"}</span>
            </DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500">
              Historique des ouvertures · Le tracking pixel ne dévoile pas l'identité précise du lecteur. Les destinataires
              ci-dessous indiquent <strong>qui a pu ouvrir</strong>.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm" data-testid="email-detail-body">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 font-mono">Historique des ouvertures</div>
                {detailLoading && <div className="text-xs text-zinc-400 italic">Chargement…</div>}
                {!detailLoading && (detail.open_events || []).length === 0 && (
                  <div className="text-xs text-zinc-400 italic">Cet email n'a pas encore été ouvert.</div>
                )}
                {!detailLoading && (detail.open_events || []).length > 0 && (
                  <>
                    {/* Iter-225 — Per-provider summary badges so we can spot
                        Gmail pre-fetch noise vs real (non-Gmail) opens. */}
                    {Object.keys(detail.provider_counts_labelled || {}).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="email-detail-provider-summary">
                        <span className="text-[11px] text-zinc-600 mr-1">Total : <strong>{detail.open_count || 0}×</strong></span>
                        {Object.entries(detail.provider_counts_labelled || {}).map(([label, n]) => (
                          <span
                            key={label}
                            data-testid={`email-detail-provider-${label}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                              label === "Gmail"
                                ? "bg-amber-100 text-amber-800 border border-amber-200"
                                : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            }`}
                            title={label === "Gmail" ? "Ouverture via Gmail (peut inclure le pré-chargement bot de Google)" : "Ouverture client direct"}
                          >
                            {label}: {n}×
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-96 overflow-auto" data-testid="email-detail-events">
                      {(detail.open_events || []).map((ev, i) => (
                        <div key={i} className="px-3 py-2 text-xs flex items-center gap-3 flex-wrap" data-testid={`email-detail-event-${i}`}>
                          <Clock size={12} className="text-emerald-600 flex-shrink-0" />
                          <span className="font-mono text-zinc-900">{(ev.at || "").replace("T", " ").slice(0, 19)}</span>
                          {ev.provider && (
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                ev.provider === "gmail"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-emerald-100 text-emerald-800"
                              }`}
                              title={ev.provider === "gmail" ? "Ouverture via Gmail — peut être un pré-chargement bot" : "Ouverture client direct (non-Gmail)"}
                            >
                              {ev.provider_label || ev.provider}
                            </span>
                          )}
                          <span className="text-zinc-700 inline-flex items-center gap-1">
                            <Eye size={11} className="text-emerald-500" /> {ev.ua_short || ev.ua || "—"}
                          </span>
                          {ev.ip && (
                            <span className="text-zinc-400 inline-flex items-center gap-1 font-mono">
                              <Globe size={11} /> {ev.ip}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {(detail.provider_counts || {}).gmail > 0 && (
                      <div className="mt-2 text-[10.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 leading-snug">
                        ⚠️ Les ouvertures « Gmail » peuvent contenir des <strong>pré-chargements automatiques</strong> par les serveurs Google (avant lecture humaine).
                        Les ouvertures hors-Gmail sont des indicateurs plus fiables d'une lecture réelle.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ReadOnlyKV({ k, v, multiline = false }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 font-mono">{k}</div>
      <div className={`px-3 py-2 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-800 text-sm select-text ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>
        {v || "—"}
      </div>
    </div>
  );
}


const Card = ({ label, value, tone }) => {
  const toneMap = {
    rose: "border-rose-200 bg-rose-50/60 text-rose-900",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    amber: "border-amber-200 bg-amber-50/60 text-amber-900",
    zinc: "border-zinc-200 bg-zinc-50/60 text-zinc-900",
  };
  return (
    <div className={`px-4 py-3 rounded-lg border ${toneMap[tone] || toneMap.zinc}`}>
      <div className="text-[11px] uppercase font-mono tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1">{value}</div>
    </div>
  );
};
